import { useEffect, useState } from "react";
import api, { formatError } from "../../lib/api";
import { toast } from "sonner";
import { Sliders, Save, RotateCcw, Loader2, AlertTriangle, Video, Phone } from "lucide-react";

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-2xl border border-slate-200 shadow-card ${className}`}>{children}</div>
);

export default function AdminConfig() {
  const [config, setConfig] = useState(null);
  const [comps, setComps] = useState([]);
  const [roleId, setRoleId] = useState("software-engineer");
  const [busy, setBusy] = useState(false);
  const [aiCfg, setAiCfg] = useState(null);

  const load = () => api.get("/admin/config").then((r) => { setConfig(r.data.config); setComps(r.data.competencies); });
  useEffect(() => {
    load();
    api.get("/admin/ai-config").then((r) => setAiCfg(r.data.config)).catch(() => {});
  }, []);

  if (!config) return <div className="min-h-[40vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  const rc = config.roles[roleId];
  const weightSum = comps.reduce((a, c) => a + (rc.weights[c.key] || 0), 0);

  const setWeight = (k, v) => setConfig({ ...config, roles: { ...config.roles, [roleId]: { ...rc, weights: { ...rc.weights, [k]: Math.max(0, Math.min(100, parseInt(v || 0))) } } } });
  const setTarget = (k, v) => setConfig({ ...config, roles: { ...config.roles, [roleId]: { ...rc, targets: { ...rc.targets, [k]: Math.max(0, Math.min(100, parseInt(v || 0))) } } } });
  const setMin = (v) => setConfig({ ...config, roles: { ...config.roles, [roleId]: { ...rc, min_ready: Math.max(0, Math.min(100, parseInt(v || 0))) } } });
  const setGap = (k, v) => setConfig({ ...config, gap_categories: { ...config.gap_categories, [k]: parseInt(v || 0) } });

  const save = async () => {
    if (weightSum !== 100) return toast.error(`Weights for ${rc.name} must sum to 100 (currently ${weightSum})`);
    setBusy(true);
    try { await api.put("/admin/config", { config }); toast.success("Configuration saved"); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const reset = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/config/reset"); setConfig(data.config); toast.success("Reset to defaults"); }
    finally { setBusy(false); }
  };

  const setAi = (mode, key, val) => setAiCfg({ ...aiCfg, [mode]: { ...aiCfg[mode], [key]: val } });
  const saveAi = async () => {
    try { await api.put("/admin/ai-config", { config: aiCfg }); toast.success("AI interview configuration saved"); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-brand mb-1">Platform Configuration</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">Readiness Engine Config</h1>
          <p className="text-ink-soft text-sm mt-1">Tune skill weights, target scores and readiness thresholds. Changes apply to all future scoring.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="reset-config" onClick={reset} disabled={busy} className="inline-flex items-center gap-2 text-sm font-bold text-ink bg-slate-100 hover:bg-slate-200 px-4 py-2.5 rounded-full transition-colors"><RotateCcw className="h-4 w-4" /> Reset</button>
          <button data-testid="save-config" onClick={save} disabled={busy} className="inline-flex items-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-2.5 rounded-full shadow-ambient transition-colors">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</button>
        </div>
      </div>

      {/* AI Interview Configuration */}
      {aiCfg && (
        <Card className="p-6" data-testid="ai-interview-config">
          <div className="flex items-center justify-between mb-4">
            <div className="font-heading font-bold text-ink flex items-center gap-2"><Sliders className="h-4 w-4 text-brand" /> AI Interview Configuration</div>
            <button data-testid="save-ai-config" onClick={saveAi} className="inline-flex items-center gap-2 text-xs font-bold text-white bg-brand hover:bg-brand-hover px-4 py-2 rounded-full transition-colors"><Save className="h-3.5 w-3.5" /> Save AI Config</button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { mode: "video", label: "AI Video Interview", Icon: Video },
              { mode: "voice", label: "AI Voice Interview", Icon: Phone },
            ].map(({ mode, label, Icon }) => {
              const m = aiCfg[mode] || {};
              return (
                <div key={mode} className="rounded-xl border border-slate-200 p-4" data-testid={`ai-config-${mode}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-semibold text-ink text-sm flex items-center gap-2"><Icon className="h-4 w-4 text-brand" /> {label}</div>
                    <label className="inline-flex items-center gap-2 text-xs font-semibold cursor-pointer">
                      <input type="checkbox" data-testid={`ai-config-${mode}-enabled`} checked={!!m.enabled} onChange={(e) => setAi(mode, "enabled", e.target.checked)} className="h-4 w-4 accent-brand" />
                      {m.enabled ? "Enabled" : "Disabled"}
                    </label>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-wider text-ink-faint mb-1">Duration (min)</div>
                      <input type="number" value={m.default_duration} onChange={(e) => setAi(mode, "default_duration", parseInt(e.target.value || 0))} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm num focus:outline-none focus:ring-2 focus:ring-brand" />
                    </div>
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-wider text-ink-faint mb-1">Credit Cost</div>
                      <input type="number" value={m.credit_cost} onChange={(e) => setAi(mode, "credit_cost", parseInt(e.target.value || 0))} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm num focus:outline-none focus:ring-2 focus:ring-brand" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Gap thresholds */}
      <Card className="p-6">
        <div className="font-heading font-bold text-ink mb-4 flex items-center gap-2"><Sliders className="h-4 w-4 text-brand" /> Skill Gap Priority Thresholds</div>
        <div className="grid grid-cols-3 gap-4 max-w-md">
          {[["critical", "Critical Gap ≥"], ["high", "High Priority ≥"], ["medium", "Medium ≥"]].map(([k, label]) => (
            <div key={k}>
              <label className="text-xs font-semibold text-ink-soft">{label}</label>
              <input data-testid={`gap-${k}`} type="number" value={config.gap_categories[k]} onChange={(e) => setGap(k, e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm num focus:outline-none focus:ring-2 focus:ring-brand" />
            </div>
          ))}
        </div>
        <p className="text-xs text-ink-faint mt-2">Gap = target − current score, in points.</p>
      </Card>

      {/* Role selector */}
      <Card className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="font-heading font-bold text-ink">Per-Role Weights & Targets</div>
          <select data-testid="config-role" value={roleId} onChange={(e) => setRoleId(e.target.value)}
            className="rounded-lg border border-slate-300 px-3.5 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-brand">
            {Object.entries(config.roles).map(([id, r]) => <option key={id} value={id}>{r.name}</option>)}
          </select>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-bold uppercase tracking-wider text-ink-faint">Skill Weights (%)</div>
              <span className={`text-xs num font-bold px-2.5 py-1 rounded-full ${weightSum === 100 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>Σ {weightSum}</span>
            </div>
            <div className="space-y-2.5">
              {comps.map((c) => (
                <div key={c.key} className="flex items-center gap-3">
                  <span className="text-sm text-ink-soft flex-1">{c.label}</span>
                  <input data-testid={`weight-${c.key}`} type="number" value={rc.weights[c.key] ?? 0} onChange={(e) => setWeight(c.key, e.target.value)}
                    className="w-20 rounded-lg border border-slate-300 px-3 py-1.5 text-sm num text-right focus:outline-none focus:ring-2 focus:ring-brand" />
                </div>
              ))}
            </div>
            {weightSum !== 100 && <div className="mt-3 flex items-center gap-1.5 text-xs text-red-600 font-semibold"><AlertTriangle className="h-3.5 w-3.5" /> Weights must total 100 to save.</div>}
          </div>

          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-ink-faint mb-3">Target Proficiency</div>
            <div className="space-y-2.5">
              {comps.map((c) => (
                <div key={c.key} className="flex items-center gap-3">
                  <span className="text-sm text-ink-soft flex-1">{c.label}</span>
                  <input data-testid={`target-${c.key}`} type="number" value={rc.targets[c.key] ?? 85} onChange={(e) => setTarget(c.key, e.target.value)}
                    className="w-20 rounded-lg border border-slate-300 px-3 py-1.5 text-sm num text-right focus:outline-none focus:ring-2 focus:ring-brand" />
                </div>
              ))}
              <div className="flex items-center gap-3 pt-2 mt-2 border-t border-slate-100">
                <span className="text-sm font-bold text-ink flex-1">Min Interview-Ready Score</span>
                <input data-testid="min-ready" type="number" value={rc.min_ready} onChange={(e) => setMin(e.target.value)}
                  className="w-20 rounded-lg border border-slate-300 px-3 py-1.5 text-sm num text-right font-bold focus:outline-none focus:ring-2 focus:ring-brand" />
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
